import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, FadeUp, MaskLines } from "./Reveal";
import { useLang } from "@/lib/i18n";

const FLAGS = {
  DE: ["#000", "#DD0000", "#FFCE00"],
  NL: ["#AE1C28", "#FFF", "#21468B"],
  BE: ["#000", "#FDDA24", "#EF3340"],
  FR: ["#002654", "#FFF", "#CE1126"],
  EU: ["#003399", "#003399", "#003399"],
};

const Flag = ({ k }) => {
  const c = FLAGS[k];
  const vertical = k === "BE" || k === "FR";
  return (
    <span className={`flex h-6 w-9 shrink-0 overflow-hidden rounded-[3px] ring-1 ring-white/20 ${vertical ? "flex-row" : "flex-col"}`}>
      {c.map((col, i) => <span key={i} className="flex-1" style={{ background: col }} />)}
    </span>
  );
};

export default function RoutesSection() {
  const { t, lang } = useLang();
  const r = t.routes;
  return (
    <section id="routes" data-testid="routes-section" className="border-t border-white/10 bg-[#0F1115]">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <Eyebrow>{r.eyebrow}</Eyebrow>
          <h2 className="font-display text-[2.5rem] font-black uppercase italic leading-[0.9] sm:text-7xl">
            <MaskLines key={lang} lines={r.title} onView />
          </h2>
          <FadeUp><p className="mt-8 max-w-md text-base leading-relaxed text-zinc-300 md:text-lg">{r.lead}</p></FadeUp>
          <div className="mt-12 space-y-6">
            {r.steps.map((s, i) => (
              <FadeUp key={s.t} delay={i * 0.1} className="flex gap-5" data-testid={`process-step-${i}`}>
                <span className="font-mono-tc text-sm font-bold text-white">0{i + 1}</span>
                <div>
                  <p className="font-display text-2xl font-bold uppercase italic">{s.t}</p>
                  <p className="mt-1 text-sm text-zinc-400">{s.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
        <ul className="self-end border-t border-white/10">
          {r.list.map((row, i) => (
            <motion.li
              key={row.k}
              data-testid={`route-row-${row.k.toLowerCase()}`}
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group relative flex items-center gap-4 overflow-hidden border-b border-white/10 py-6 sm:gap-6 sm:py-8"
            >
              <span className="absolute inset-0 origin-bottom scale-y-0 bg-white transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-y-100" />
              <span className="relative font-mono-tc text-xs text-zinc-400 transition-colors group-hover:text-black">PL ↔ {row.k}</span>
              <span className="relative"><Flag k={row.k} /></span>
              <span className="relative flex-1 font-display text-4xl font-black uppercase italic transition-all duration-500 group-hover:translate-x-2 group-hover:text-black sm:text-6xl">{row.c}</span>
              <span className="relative hidden font-mono-tc text-xs uppercase tracking-[0.15em] text-zinc-400 transition-colors group-hover:text-black md:block">{row.n}</span>
              <ArrowUpRight className="relative h-6 w-6 text-white transition-all duration-500 group-hover:rotate-45 group-hover:text-black" />
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
